"use client";

import * as React from "react";
import type { Icon as TPhosphorIconComponent } from "@phosphor-icons/react";
import * as PhosphorIcons from "@phosphor-icons/react";

export type TPhosphorWeight =
  | "thin"
  | "light"
  | "regular"
  | "bold"
  | "fill"
  | "duotone";

interface IPhosphorIconProps
  extends Omit<React.ComponentProps<TPhosphorIconComponent>, "color"> {
  /**
   * Phosphor icon name in kebab-case or PascalCase (e.g. "users", "heart", "paper-plane-tilt").
   * Callers store these strings in CMS content; we convert to PascalCase to find the component.
   */
  name: string;
  weight?: TPhosphorWeight;
  color?: string;
}

/**
 * Renders a Phosphor icon by name.
 * Falls back to `Circle` if the name is unrecognized.
 */
export function PhosphorIcon({
  name,
  weight = "duotone",
  color = "currentColor",
  ...props
}: IPhosphorIconProps): React.ReactElement {
  const pascalName = kebabToPascal(name);
  const iconsByName = PhosphorIcons as unknown as Record<
    string,
    TPhosphorIconComponent
  >;
  const Icon =
    iconsByName[pascalName] ?? PhosphorIcons.Circle;

  return <Icon {...props} weight={weight} color={color} />;
}

function kebabToPascal(str: string): string {
  return str
    .split("-")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join("");
}
